// Density fit: fit a type's instances onto where they agree. It gets each instance's points (world,
// before its frame corrections W and its fit F), grouped by source frame, and returns the new F and W;
// the editor commits them.
//
// Support of a point: how many other instances have a point within 3 cm. The target for an instance is
// the other instances' points that at least a quarter of them agree on. Each round nudges every
// instance's fit (not the anchor's) onto it, then every instance's frames.
//
// The heavy parts are tasks (runTask): support over a range of points, and fits of one instance. The
// caller passes exec, which runs them inline or on a pool of workers (densityworker.ts); tasks of one
// step are independent, so the result is the same either way.
import { type Rigid, compose, identity, inverse, transformPoints } from '../align.ts';
import { type FrameFit, type Fitted, type MakeNN, target, support, pointDensity, fitFrames, fitOnto } from './instances.ts';
import { type PoleFit, type PoleOpts, fitPole } from '../fit/pole.ts';
import { type SurfaceFit, type SurfaceOpts, fitSurface } from '../fit/surface.ts';

export interface DFrame { key: string; W: Rigid | null; xyz: Float32Array; nrm: Float32Array; fit: boolean }
export interface DInst { name: string; F: Rigid; anchor: boolean; frames: DFrame[] }
export interface DResult {
  F: Record<string, Rigid>;                    // instances whose fit changed
  W: Record<string, Record<string, Rigid>>;    // instance -> frame key -> its new correction
  before: number; after: number;               // agreement (0-1): mean share of the other instances that agree
  ni: number; nf: number;                      // instance fits and frame corrections made
}
export type Progress = (text: string, frac: number) => void;

export type Task =
  | { kind: 'support'; xyz: Float32Array; inst: Int32Array; from: number; to: number }
  | { kind: 'pdensity'; sample: Float32Array; step: number; xyz: Float32Array; r: number }
  | { kind: 'fit'; xyz: Float32Array; nrm: Float32Array; list: { key: string; xyz: Float32Array }[]; opts?: { minPts?: number; maxShift?: number; maxYaw?: number } }
  | { kind: 'onto'; xyz: Float32Array; nrm: Float32Array; src: Float32Array }
  | { kind: 'shape'; shape: 'pole' | 'surface'; xyz: Float32Array; nrm: Float32Array; opts: PoleOpts & SurfaceOpts };
export type TaskResult = Uint16Array | FrameFit<string>[] | Fitted | PoleFit | SurfaceFit | null;
export type Exec = (t: Task) => Promise<TaskResult>;

/** Run one task (in a worker, or inline). A fit with too thin a target gives null.
 * support: support of points from..to; fit: fitFrames of a list of point sets (frames, or one whole
 * instance) onto a target; pdensity: point density (pointDensity) of a slice of points; onto: fitOnto (an instance placed from scratch); shape: a pole or surface
 * fitted to one part's points (src/fit). */
export function runTask(t: Task, mk?: MakeNN): TaskResult {
  if (t.kind === 'support') return support(t.xyz, t.inst, 0.03, 150000, t.from, t.to);
  if (t.kind === 'pdensity') return pointDensity(t.sample, t.step, t.xyz, t.r);
  if (t.kind === 'shape') return t.shape === 'pole' ? fitPole(t.xyz, t.opts) : fitSurface(t.xyz, t.nrm, t.opts);
  const tg = target({ xyz: t.xyz, nrm: t.nrm, rgb: new Uint8Array(0) }, undefined, undefined, mk);
  if (!tg) return null;
  if (t.kind === 'onto') return fitOnto(t.src, tg);   // a whole instance, from scratch (8 starting turns)
  const g = fitFrames(t.list, tg, { minPts: 150, ...t.opts });
  for (;;) { const r = g.next(); if (r.done) return r.value; }
}

const yieldNow = () => new Promise(r => setTimeout(r, 0));

/** fn over items, at most limit at a time (results in order). */
async function mapLimit<T, R>(items: T[], limit: number, fn: (x: T, i: number) => Promise<R>): Promise<R[]> {
  const out: R[] = new Array(items.length);
  let next = 0;
  await Promise.all(Array.from({ length: Math.max(1, Math.min(limit, items.length)) }, async () => {
    while (next < items.length) { const i = next++; out[i] = await fn(items[i], i); }
  }));
  return out;
}

/** One instance's frame placed in the overlay: F · W · P. */
function place(i: DInst, f: DFrame) {
  const M = f.W ? compose(i.F, f.W) : i.F;
  return { xyz: transformPoints(M, f.xyz), nrm: transformPoints(M, f.nrm, undefined, false) };
}

export async function densityFit(insts: DInst[], rounds = 3, progress: Progress = () => {},
  exec: Exec = async t => runTask(t), workers = 1): Promise<DResult> {
  const F0 = new Map(insts.map(i => [i.name, i.F])), W0 = new Map(insts.flatMap(i => i.frames.map(f => [`${i.name}\u0000${f.key}`, f.W] as const)));
  const parts = Math.max(1, workers * 2), steps = (2 * rounds + 1) * parts + rounds * 2 * insts.length;
  let done = 0, ni = 0, nf = 0;
  const tick = (text: string) => progress(text, Math.min(1, ++done / steps));

  /** Support of every placed point (split over the workers), the agreement score, and each instance's target points. */
  const measure = async (what: string) => {
    const placed = insts.map(i => i.frames.map(f => place(i, f)));
    const n = placed.reduce((a, fs) => a + fs.reduce((b, p) => b + p.xyz.length / 3, 0), 0);
    const xyz = new Float32Array(3 * n), nrm = new Float32Array(3 * n), inst = new Int32Array(n);
    let o = 0;
    placed.forEach((fs, k) => { for (const p of fs) { const m = p.xyz.length / 3; xyz.set(p.xyz, 3 * o); nrm.set(p.nrm, 3 * o); inst.fill(k, o, o + m); o += m; } });
    const cuts = Array.from({ length: parts + 1 }, (_, i) => Math.round((i * n) / parts));
    const chunks = await mapLimit(cuts.slice(0, -1), parts, async (from, i) => {
      const r = await exec({ kind: 'support', xyz, inst, from, to: cuts[i + 1] }) as Uint16Array;
      tick(what);
      return r;
    });
    const sup = new Uint16Array(n);
    chunks.forEach((c, i) => sup.set(c, cuts[i]));
    const others = Math.max(1, insts.length - 1), need = Math.max(1, Math.ceil(0.25 * (insts.length - 1)));
    let agree = 0;
    for (const s of sup) agree += s / others;
    agree /= Math.max(1, n);
    /** The target for instance k: the other instances' points enough of them agree on. */
    const targetOf = (k: number) => {
      let c = 0;
      for (let j = 0; j < n; j++) if (inst[j] !== k && sup[j] >= need) c++;
      const px = new Float32Array(3 * c), pn = new Float32Array(3 * c);
      for (let j = 0, q = 0; j < n; j++) if (inst[j] !== k && sup[j] >= need) { px.set(xyz.subarray(3 * j, 3 * j + 3), 3 * q); pn.set(nrm.subarray(3 * j, 3 * j + 3), 3 * q); q++; }
      return { px, pn };
    };
    return { placed, agree, targetOf };
  };

  progress('measuring where the instances agree…', 0);
  const before = (await measure('measuring where the instances agree…')).agree;
  for (let r = 1; r <= rounds; r++) {
    // the instances: one rigid nudge each, from where they are now (all against this round's measure)
    let m = await measure(`round ${r}/${rounds}: measuring`);
    await mapLimit(insts, workers, async (ins, k) => {
      if (!ins.anchor) {
        const all = m.placed[k], n = all.reduce((a, p) => a + p.xyz.length / 3, 0);
        if (n >= 150) {
          const xyz = new Float32Array(3 * n);
          let o = 0;
          for (const p of all) { xyz.set(p.xyz, o); o += p.xyz.length; }
          const { px, pn } = m.targetOf(k);
          await yieldNow();
          const f = (await exec({ kind: 'fit', xyz: px, nrm: pn, list: [{ key: ins.name, xyz }] }) as FrameFit<string>[] | null)?.[0];
          if (f && !f.skip) { ins.F = compose(f.W, ins.F); ni++; }
        }
      }
      tick(`round ${r}/${rounds}: fitting the instances`);
    });
    // their frames: each instance's points from each frame, on their own
    m = await measure(`round ${r}/${rounds}: measuring`);
    await mapLimit(insts, workers, async (ins, k) => {
      const list = ins.frames.map((f, j) => ({ key: String(j), xyz: m.placed[k][j].xyz })).filter((_, j) => ins.frames[j].fit);
      if (list.length) {
        const { px, pn } = m.targetOf(k);
        await yieldNow();
        for (const g of (await exec({ kind: 'fit', xyz: px, nrm: pn, list }) as FrameFit<string>[] | null) ?? []) {
          if (g.skip) continue;
          const f = ins.frames[+g.key];
          f.W = compose(inverse(ins.F), compose(g.W, compose(ins.F, f.W ?? identity())));   // W' = F⁻¹ · G · F · W
          nf++;
        }
      }
      tick(`round ${r}/${rounds}: fitting the frames`);
    });
  }
  const after = (await measure('measuring the result…')).agree;
  const res: DResult = { F: {}, W: {}, before, after, ni, nf };
  for (const i of insts) {
    if (i.F !== F0.get(i.name)) res.F[i.name] = i.F;
    for (const f of i.frames) if (f.W && f.W !== W0.get(`${i.name}\u0000${f.key}`)) (res.W[i.name] ??= {})[f.key] = f.W;
  }
  progress('done', 1);
  return res;
}
